import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { toastDismissed, type Toast, type ToastTone } from '@/features/ui/uiSlice'
import { cn } from '@/lib/cn'

const TONES: Record<ToastTone, { icon: typeof Info; accent: string; text: string }> = {
  success: { icon: CheckCircle2, accent: 'bg-positive', text: 'text-positive' },
  error: { icon: AlertCircle, accent: 'bg-negative', text: 'text-negative' },
  info: { icon: Info, accent: 'bg-info', text: 'text-info' }
}

const LIFETIME_MS = 4200

function ToastCard({ toast }: { toast: Toast }): ReactNode {
  const dispatch = useAppDispatch()
  const { icon: Icon, accent, text } = TONES[toast.tone]

  useEffect(() => {
    const timer = setTimeout(() => dispatch(toastDismissed(toast.id)), LIFETIME_MS)
    return () => clearTimeout(timer)
  }, [dispatch, toast.id])

  return (
    <div
      role="status"
      className="pointer-events-auto relative flex w-80 animate-slide-up items-start gap-2.5 overflow-hidden rounded-md border border-line bg-surface p-3 pl-3.5 shadow-2xl"
    >
      <span className={cn('absolute left-0 top-0 h-full w-1', accent)} aria-hidden />
      <Icon size={16} className={cn('mt-px shrink-0', text)} aria-hidden />
      <p className="flex-1 text-[12px] leading-snug text-fg">
        {toast.message}
      </p>
      <button
        onClick={() => dispatch(toastDismissed(toast.id))}
        className="shrink-0 rounded p-0.5 text-subtle transition-colors hover:bg-raised hover:text-fg"
        aria-label="Dismiss"
      >
        <X size={13} />
      </button>
    </div>
  )
}

export function Toaster(): ReactNode {
  const toasts = useAppSelector((s) => s.ui.toasts)
  if (!toasts.length) return null
  return createPortal(
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2">
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>,
    document.body
  )
}
